import { useGateValue } from "@statsig/react-bindings";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toUrnString } from "../../types/urn.ts";
import { useCanvasActionContext } from "../canvas/canvasContext.tsx";
import { artifactQueries } from "../../hooks/artifactQueries.ts";
import { useArtifact } from "../../hooks/useArtifacts.tsx";
import { useIsMobile } from "../../hooks/use-mobile.tsx";
import { threadQueries, updateThreadToInProgress } from "../../hooks/useThreads.tsx";
import { useIsSharedThread } from "../../hooks/useIsSharedThread.ts";
import { iconForName } from "./iconForName.ts";
import { cn, resolveApiPath } from "../../lib/utils.ts";
import { getShareTokenFromPath, toSharedAssetPath } from "../../lib/sharedThread.ts";
import type { ArtifactAction, HeavyArtifactResponse } from "../../types/artifacts.ts";
import { artifactDisplayTitle, artifactExpandedTitle } from "../../shared/artifactTitle.ts";
import { ArtifactErrorBoundary } from "./ArtifactErrorBoundary.tsx";
import {
  isArtifactFinalStatus,
  isArtifactReadOnly,
  isBrowserSignInArtifact,
} from "./artifactUtils.ts";
import { ArtifactView } from "./view/ArtifactView.tsx";
import { FullscreenArtifactScroll } from "./FullscreenArtifactScroll.tsx";
import { fireArtifactAction } from "./ArtifactActionsButtons.tsx";
import { useSetBrowsedArtifact } from "./browsedArtifact.ts";
import { ArtifactChip } from "./ArtifactChip.tsx";
import { HeavyArtifactLightbox } from "./HeavyArtifactLightbox.tsx";
import { useLightboxUrlState } from "./useLightboxUrlState.ts";

import { isCancelledOrSuperseded } from "@/types/artifacts-shared.ts";
import { isPreviewableImageType } from "@/types/files.ts";

export type HeavyArtifactItemProps = {
  threadId: number;
  messageId: number;
  msgListArtifact: HeavyArtifactResponse;
  closeArtifactGroup?: () => void;
};

type ArtifactBridgeAction = {
  action: string;
  label: string;
  variant: "primary" | "secondary";
};

// Derive primary + secondary buttons for the native header from the server's
// declarative `actions[]`. Returns null for flows the bridge can't render as
// two POST buttons: artifacts with an external-URL action (OAuth connect) or
// with a 3-button flow (invitationReceived's Accept / Decline / Counter)
// keep their in-webview footer.
function getArtifactBridgeActions(
  actions: ArtifactAction[] | undefined,
): ArtifactBridgeAction[] | null {
  if (actions?.length !== 2) return null;
  if (actions.some((a) => a.method === "GET_EXTERNAL")) return null;
  const [primary, secondary] = actions;
  return [
    { action: primary.id, label: primary.label, variant: "primary" },
    { action: secondary.id, label: secondary.label, variant: "secondary" },
  ];
}

// Renders the artifact content through the generic schema-driven
// `<ArtifactView>`. Every heavy artifact carries a server-built `view`, and the
// renderer dispatches on the view's component types — so adding a new artifact
// type is a backend-only change.
const ArtifactContent = ({
  heavyArtifact,
  maximized,
  threadId,
  messageId,
  onRequestClose,
}: {
  heavyArtifact: HeavyArtifactResponse;
  maximized: boolean;
  threadId: number;
  messageId: number;
  onRequestClose?: () => void;
}) => {
  const isSharedThread = useIsSharedThread();

  if (!heavyArtifact.view) return null;

  const isReadOnly = isArtifactReadOnly(heavyArtifact, isSharedThread);
  return (
    <FullscreenArtifactScroll maximized={maximized}>
      <ArtifactView
        view={heavyArtifact.view}
        threadId={threadId}
        messageId={messageId}
        artifactId={heavyArtifact.id}
        isReadOnly={isReadOnly}
        isSharedThread={isSharedThread}
        maximized={maximized}
        onRequestClose={onRequestClose}
      />
    </FullscreenArtifactScroll>
  );
};

/**
 * Inline image rendering for an image-type `file` artifact: show the picture
 * itself instead of the generic chip — you uploaded an image, so show the
 * image. Still a button into the same lightbox (filename, summary, download).
 * The preview path mirrors the server's `file` view (`api/.../artifactViews/file.ts`);
 * `resolveApiPath` makes it absolute against the API origin.
 */
const HeavyArtifactImage = ({
  src,
  title,
  isCancelled,
  onOpen,
  onError,
}: {
  src: string;
  title: string;
  isFinalStatus: boolean;
  isCancelled: boolean;
  onOpen: () => void;
  /** Fires if the image fails to load so the caller can fall back to the chip
   *  rather than leave a blank bordered box. */
  onError: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${title}`}
      className={cn(
        "block max-w-full overflow-hidden self-end cursor-pointer",
        isCancelled && "grayscale",
      )}
    >
      {/* Natural aspect, capped — a wide screenshot stays wide, a tall one stays
          tall, neither cropped nor letterboxed. */}
      {/* `loading="lazy"` defers offscreen images in long threads; `min-h-16`
          reserves roughly the chip's height so the row doesn't collapse-then-jump
          while the image loads. */}
      <img
        src={src}
        alt={title}
        loading="lazy"
        onError={onError}
        className="block max-h-80 min-h-16 w-auto max-w-full object-contain"
      />
    </button>
  );
};

export const HeavyArtifactItem = ({
  threadId,
  messageId,
  msgListArtifact,
  closeArtifactGroup,
}: HeavyArtifactItemProps) => {
  const queryClient = useQueryClient();
  const isSharedThread = useIsSharedThread();
  const isMobile = useIsMobile();
  const { artifact } = useArtifact({
    threadId,
    messageId,
    artifact: msgListArtifact,
  });
  const [isLightboxOpen, setIsLightboxOpen] = useLightboxUrlState("inline", artifact.id);

  const heavyArtifact = artifact as HeavyArtifactResponse;
  const userIsAdmin = useGateValue("internal-features");
  const serverActions =
    "actions" in heavyArtifact && heavyArtifact.actions ? heavyArtifact.actions : undefined;
  const bridgeActions = useMemo(() => getArtifactBridgeActions(serverActions), [serverActions]);
  const displayTitle = artifactDisplayTitle(heavyArtifact);
  // The native header the bridge hands this to is an expanded surface, not a
  // chip — it has the room to ask the card's question.
  const bridgedTitle = artifactExpandedTitle(heavyArtifact);

  const handleLightboxOpenChange = useCallback(
    async (open: boolean) => {
      // Fetch fresh data on both open and close to sync edits bi-directionally
      // (refetchQueries won't work because query is disabled for non-polling artifacts).
      // fetchQuery both fetches and populates the cache under the detail key.
      try {
        await queryClient.fetchQuery(artifactQueries.detail(threadId, messageId, artifact.id));
      } catch (error) {
        console.error("Failed to refresh artifact:", error);
      }
      // Notify the native app so it can hide the native ChatInput, swap the
      // header to an artifact view, and (if we sent action metadata) render
      // Cancel/Send buttons in the native header.
      if (window.isWebView) {
        if (open) {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: "artifactEditorOpen",
              title: bridgedTitle,
              actions: bridgeActions,
            }),
          );
        } else {
          window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "artifactEditorClose" }));
        }
      }
      setIsLightboxOpen(open);
    },
    [queryClient, threadId, messageId, artifact.id, bridgedTitle, bridgeActions, setIsLightboxOpen],
  );

  const canvas = useCanvasActionContext();
  const setBrowsedArtifact = useSetBrowsedArtifact();
  // TODO(BIG-6188): mobile should route to a full-page artifact view rather than
  // falling back to this lightbox.
  /**
   * Open the artifact: on desktop with the canvas available, present it in the
   * canvas — the single place an artifact opens — instead of this in-thread
   * lightbox. The canvas is undefined on mobile / gate-off and `resetTo` returns
   * false for URNs it can't render, so both fall through to the local lightbox.
   *
   * Publishing the artifact points the omnibox card at it too, so the two
   * surfaces describe the same thing. Only on the canvas path: the lightbox is
   * already the whole artifact, and the card behind it has nothing to add.
   */
  const openArtifact = useCallback(() => {
    const urn = toUrnString({ type: heavyArtifact.artifactType, id: artifact.id });
    if (isBrowserSignInArtifact(heavyArtifact) && heavyArtifact.biscuit && !window.isWebView) {
      if (canvas?.getSnapshot()?.canvasItem === urn) canvas.close();
      canvas?.dropContent(urn);
      setBrowsedArtifact({ threadId, artifact: heavyArtifact });
      return;
    }
    if (canvas?.resetTo(urn)) {
      setBrowsedArtifact({ threadId, artifact: heavyArtifact });
      return;
    }
    void handleLightboxOpenChange(true);
  }, [canvas, threadId, heavyArtifact, artifact.id, handleLightboxOpenChange, setBrowsedArtifact]);

  const Icon = iconForName(heavyArtifact.iconName);

  // Instant Book kickoff — flag it with a badge on the chip / inline header so
  // the compact surface matches the expanded view. Narrow on artifactType so
  // `autoSchedule` reads off the SchedulingKickoffArtifact member, not `any`.
  const isInstantBook =
    heavyArtifact.artifactType === "schedulingKickoff" && heavyArtifact.autoSchedule === true;

  const isFinalStatus = isArtifactFinalStatus(heavyArtifact);

  // Image-type file artifact → render the picture inline instead of the chip.
  const fileSourceId = heavyArtifact.artifactType === "file" ? heavyArtifact.sourceId : undefined;
  // Scope the load-failure to the specific source rather than a bare boolean: if
  // the artifact re-fetches with a different sourceId, the image is retried
  // automatically (a transient blip — e.g. the preview endpoint not ready right
  // after upload — doesn't permanently pin this instance to the chip). Derived,
  // not a useEffect that resets on sourceId change — exhaustive-deps strips a
  // trigger-only dep that the effect body never reads.
  const [failedSourceId, setFailedSourceId] = useState<string | undefined>(undefined);
  const imageLoadFailed = fileSourceId !== undefined && failedSourceId === fileSourceId;
  const isImageFileArtifact =
    !imageLoadFailed &&
    heavyArtifact.artifactType === "file" &&
    isPreviewableImageType(heavyArtifact.mimeType) &&
    !!fileSourceId;
  // On a shared thread the session-authed `/a/...` preview 401s, so rewrite to the
  // public share-token-scoped path (authorized server-side by snapshot membership).
  const imagePreviewUrl = (() => {
    if (!isImageFileArtifact || !fileSourceId) return null;
    const authedPath = `/api/a/files/preview/source/${fileSourceId}?w=800`;
    if (!isSharedThread) return resolveApiPath(authedPath);
    const shareToken = getShareTokenFromPath();
    return shareToken ? resolveApiPath(toSharedAssetPath(authedPath, shareToken)) : null;
  })();

  // Dispatch a native-header tap by looking up the matching server action
  // (by `id`) and POSTing it. Mirrors the in-webview footer's fire flow
  // (`HeavyArtifactLightbox` → `ArtifactActionsButtons.onActionFire`):
  // optimistically flip the thread to in-progress, close the editor, roll
  // back on failure via `threadQueries.all()` invalidation.
  const handleBridgeAction = useCallback(
    async (actionId: string) => {
      const target = serverActions?.find((a) => a.id === actionId);
      if (!target) return;
      if (!target.reusable) updateThreadToInProgress(queryClient, threadId, target.pendingBody);
      if (window.isWebView) {
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "artifactEditorClose" }));
      }
      setIsLightboxOpen(false);
      closeArtifactGroup?.();
      try {
        await fireArtifactAction(target);
      } catch (error) {
        console.error("Artifact action failed:", error);
        void queryClient.invalidateQueries({ queryKey: threadQueries.all() });
      }
    },
    [serverActions, queryClient, threadId, closeArtifactGroup, setIsLightboxOpen],
  );

  // Listen for actions dispatched from the native header's buttons (and for
  // the native back gesture wanting to close the editor). Only the currently
  // open artifact has the listener attached.
  useEffect(() => {
    if (!isLightboxOpen || !window.isWebView) return;

    const onAction = (e: Event) => {
      const detail = (e as CustomEvent<{ action?: string }>).detail;
      if (!detail?.action) return;
      void handleBridgeAction(detail.action);
    };
    const onClose = () => {
      void handleLightboxOpenChange(false);
    };
    window.addEventListener("nativeArtifactAction", onAction);
    window.addEventListener("nativeArtifactClose", onClose);
    return () => {
      window.removeEventListener("nativeArtifactAction", onAction);
      window.removeEventListener("nativeArtifactClose", onClose);
    };
  }, [isLightboxOpen, handleBridgeAction, handleLightboxOpenChange]);

  // Native biscuit summary tap → open this artifact's fullscreen lightbox.
  // The native `ArtifactBiscuitSection` lives outside the WebView, so it
  // can't call setIsLightboxOpen directly; instead it fires a CustomEvent
  // and the HeavyArtifactItem whose `artifact.id` matches opens its own
  // lightbox via the existing handler (which also posts `artifactEditorOpen`
  // back to native so the native header swaps in).
  useEffect(() => {
    if (!window.isWebView) return;
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ artifactId?: string }>).detail;
      if (detail?.artifactId !== artifact.id) return;
      // Guard against re-entry: handleLightboxOpenChange runs a refetch and
      // re-posts `artifactEditorOpen` to native, which would briefly flicker
      // the native header if the lightbox is already open.
      if (isLightboxOpen) return;
      void handleLightboxOpenChange(true);
    };
    window.addEventListener("nativeBiscuitOpenFullscreen", onOpen);
    return () => window.removeEventListener("nativeBiscuitOpenFullscreen", onOpen);
  }, [artifact.id, handleLightboxOpenChange, isLightboxOpen]);

  // When the native header has taken over on mobile webview, hide the in-dialog
  // header (title is now in the native header) and — if we sent bridge actions —
  // the in-dialog footer (buttons are now in the native header).
  const nativeHeaderTookOver = isMobile && window.isWebView === true;
  const hideInDialogHeader = nativeHeaderTookOver;
  const hideInDialogFooter = nativeHeaderTookOver && bridgeActions !== null;

  return (
    <div className="w-full flex flex-col gap-2">
      <HeavyArtifactLightbox
        artifact={heavyArtifact}
        threadId={threadId}
        messageId={messageId}
        open={isLightboxOpen}
        onOpenChange={handleLightboxOpenChange}
        // Skip the async refetch+bridge path on action fire — the dialog
        // must close instantly, mirroring the pre-refactor behavior where
        // `handleArtifactActionFire` called `setIsLightboxOpen(false)`
        // directly instead of routing through `handleLightboxOpenChange`.
        onImmediateClose={() => setIsLightboxOpen(false)}
        closeArtifactGroup={closeArtifactGroup}
        hideHeader={hideInDialogHeader}
        hideFooter={hideInDialogFooter}
      >
        <ArtifactErrorBoundary
          artifact={heavyArtifact}
          threadId={threadId}
          messageId={messageId}
          userIsAdmin={userIsAdmin}
        >
          <ArtifactContent
            heavyArtifact={heavyArtifact}
            maximized={true}
            threadId={threadId}
            messageId={messageId}
            onRequestClose={() => handleLightboxOpenChange(false)}
          />
        </ArtifactErrorBoundary>
      </HeavyArtifactLightbox>

      {/* Product recommendations must be readable without opening an attachment. */}
      {heavyArtifact.artifactType === "shoppingResults" && heavyArtifact.view && !isSharedThread ? (
        <ArtifactErrorBoundary
          artifact={heavyArtifact}
          threadId={threadId}
          messageId={messageId}
          userIsAdmin={userIsAdmin}
        >
          <ArtifactView
            view={heavyArtifact.view}
            threadId={threadId}
            messageId={messageId}
            artifactId={heavyArtifact.id}
            isReadOnly={isArtifactReadOnly(heavyArtifact, isSharedThread)}
            isSharedThread={isSharedThread}
          />
        </ArtifactErrorBoundary>
      ) : imagePreviewUrl ? (
        <HeavyArtifactImage
          src={imagePreviewUrl}
          title={displayTitle}
          isFinalStatus={isFinalStatus}
          isCancelled={isCancelledOrSuperseded(heavyArtifact.status)}
          onOpen={openArtifact}
          onError={() => setFailedSourceId(fileSourceId)}
        />
      ) : (
        <ArtifactChip
          icon={Icon}
          title={displayTitle}
          statusLabel={heavyArtifact.statusLabel}
          isFinalStatus={isFinalStatus}
          isCancelled={isCancelledOrSuperseded(heavyArtifact.status)}
          isInstantBook={isInstantBook}
          onOpen={openArtifact}
        />
      )}
    </div>
  );
};

export default HeavyArtifactItem;
